import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../services/api';
import { OrderProduct } from '../types/order';

interface DIDMapping {
  block_size: number;
  product_id: string;
}

interface OrderingVoiceDIDTabProps {
  onError?: (msg: string) => void;
}

const OrderingVoiceDIDTab: React.FC<OrderingVoiceDIDTabProps> = ({ onError }) => {
  const [mappings, setMappings] = useState<DIDMapping[]>([]);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [newBlockSize, setNewBlockSize] = useState('');
  const [newProductId, setNewProductId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mappingRes, productRes] = await Promise.all([
        api.getDidProductMappings(),
        api.adminListProducts(),
      ]);
      setMappings(mappingRes.mappings ?? []);
      setProducts((productRes || []).filter((p) => p.is_active));
    } catch (e: unknown) {
      onError?.((e as Error)?.message || 'Failed to load DID settings');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAddRow = () => {
    const size = parseInt(newBlockSize, 10);
    if (!Number.isFinite(size) || size < 1) {
      setSaveError('Block size must be a positive integer.');
      return;
    }
    if (!newProductId) {
      setSaveError('Select a product for this mapping.');
      return;
    }
    setSaveError(null);
    setMappings((prev) => {
      const next = [...prev, { block_size: size, product_id: newProductId }];
      return next.sort((a, b) => b.block_size - a.block_size);
    });
    setNewBlockSize('');
    setNewProductId('');
  };

  const handleDelete = (idx: number) => {
    setMappings((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const result = await api.setDidProductMappings(mappings);
      setMappings(result.mappings ?? []);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e: unknown) {
      setSaveError((e as any)?.response?.data?.detail || (e as Error)?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const productById: Record<string, OrderProduct> = {};
  products.forEach((p) => { productById[p.id] = p; });

  const selectedNewProduct = products.find((p) => p.id === newProductId) ?? null;

  return (
    <Box sx={{ maxWidth: 700 }}>
      <Typography variant="h6" gutterBottom>
        Voice / DID auto-inject mappings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When a voice order includes DIDs, the wizard automatically injects DID products using a greedy
        algorithm — largest block first, then smaller blocks for the remainder. For example, 12 DIDs
        with blocks of 10 and 1 produces 1 × DID10 + 2 × DID1. Configure one mapping per block size.
      </Typography>

      {loading ? (
        <Typography variant="body2">Loading…</Typography>
      ) : (
        <>
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Saved successfully.
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={120}>Block size</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell width={56} />
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">
                        No mappings configured. Add one below.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {mappings.map((m, idx) => {
                  const prod = productById[m.product_id];
                  return (
                    <TableRow key={idx}>
                      <TableCell>{m.block_size}</TableCell>
                      <TableCell>
                        {prod ? (
                          <>
                            <Typography variant="body2" fontWeight={500}>{prod.name}</Typography>
                            {prod.monthly_price != null && (
                              <Typography variant="caption" color="text.secondary">
                                ${Number(prod.monthly_price).toFixed(2)}/mo
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">{m.product_id}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleDelete(idx)} aria-label="Remove mapping">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add mapping
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <TextField
                label="Block size"
                type="number"
                size="small"
                value={newBlockSize}
                onChange={(e) => setNewBlockSize(e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 160 }}
                helperText="How many DIDs this product covers"
              />
              <Autocomplete
                options={products}
                getOptionLabel={(p) =>
                  p.name + (p.monthly_price != null ? ` — $${Number(p.monthly_price).toFixed(2)}/mo` : '')
                }
                value={selectedNewProduct}
                onChange={(_e, val) => setNewProductId(val?.id ?? '')}
                size="small"
                sx={{ minWidth: 320, flexGrow: 1 }}
                renderInput={(params) => (
                  <TextField {...params} label="DID product" />
                )}
              />
              <Button variant="outlined" onClick={handleAddRow} sx={{ mt: 0.5 }}>
                Add
              </Button>
            </Box>
          </Paper>

          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save mappings'}
          </Button>
        </>
      )}
    </Box>
  );
};

export default OrderingVoiceDIDTab;
